import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { ResponseMonthlyMemberReport, ResponseMonthlyTeamReport } from '../../../../../states/types/report'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import Loading from '../../../common/Loading'
import DeviationHeader from '../DeviationHeader'
import Deviation from '../Deviation'
import MonthlyBarGraph from './MonthlyBarGraph'
import { useTranslation } from 'react-i18next'
import { format } from '../../../../../utils/date'
import { clone } from 'lodash'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const MonthlyGrowth: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [color, setColor] = useState<string>()
  const [workStyleId, setWorkStyleId] = useState<number>(0)
  const [occupationId, setOccupationId] = useState<number>(0)

  const canvasRef = useRef<HTMLCanvasElement>(null)

  const targetIndicatorValues = (indicatorName: string) => ({
    current: data?.indicators?.find((value) => value.label === indicatorName)?.current ?? null,
    prev: data?.indicators?.find((value) => value.label === indicatorName)?.past ?? null,
    team: data?.indicators?.find((value) => value.label === indicatorName)?.team ?? null,
    company: data?.indicators?.find((value) => value.label === indicatorName)?.company ?? null,
    occupation: data?.indicators?.find((value) => value.label === indicatorName)?.occupation ?? null,
    qasee: data?.indicators?.find((value) => value.label === indicatorName)?.qasee ?? null,
    checked: data?.indicators?.find((value) => value.label === indicatorName)?.checked || false,
    appropriate_from: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_from ?? null,
    appropriate_to: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_to ?? null,
  })

  const displayDayList = useMemo(() => {
    if (!data?.date?.from || !data?.date?.to) return []
    const dateList = []
    for (let d = clone(new Date(data.date.from));d <= new Date(data.date.to);d.setDate(d.getDate() + 1)) {
      if (format(d, "d") === "0") {
        dateList.push(format(d, "YYYY-MM-DD"))
      }
    }
    return Array.from(new Set(dateList))
  }, [data?.date?.from, data?.date?.to])

  useEffect(() => {
    const width = canvasRef.current?.clientWidth || 0
    const height = canvasRef.current?.clientHeight || 0
    const canvas = canvasRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.width = width
    canvas.height = height
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = "#008FFB"
    displayDayList.forEach((value, index) => {
      data?.improvement_progress?.values.filter((v) => v.date === value).forEach((val) => {
        ctx.lineTo((width / ((displayDayList.length || 1) - 1)) * (index), (height / 100) * (100 - val.value))
      })
    })
    ctx.stroke()
  }, [data])

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
      setScores(report.monthlyTeamReport.scores?.find((score) => score.label === "improvement_score"))
      setColor("")
      setWorkStyleId(0)
      setOccupationId(0)
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
      setScores(report.monthlyMemberReport.scores?.find((score) => score.label === "improvement_score"))
      setColor(report.monthlyMemberReport.member?.applied_occupation?.icon_color)
      setWorkStyleId(0)
      setOccupationId(0)
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_growth" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="improvement" className="content-block monthly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="prefix">
                      エンゲージメント
                      <br />
                      【活動度】
                    </div>
                    <div className="title">
                      セルフマネジメント指数
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      Qaseeの活用状況から改善に対する行動を測定し、各業務適正度の改善状況と合わせてセルフマネジメント観点での評価を行なっています。
                    </div>
                  </div>
                  <MonthlyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                      distributions: scores?.distributions || [],
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        セルフマネジメント
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        is_standard
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="qasee_use_time"
                        display_prefix="Qasee"
                        display_title="アクセス時間"
                        type="minute"
                        values={targetIndicatorValues("qasee_use_time")}
                        labels={[0, 5, 10, 15, 20, 25, 30]}
                        upper_limit
                        appropriate={33.3}
                        appropriate_position={16.5}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="qasee_access_count"
                        display_prefix="Qasee"
                        display_title="アクセス回数"
                        type="count"
                        values={targetIndicatorValues("qasee_access_count")}
                        labels={[0, 5, 10, 15, 20, 25, 30]}
                        upper_limit
                        appropriate={33.3}
                        appropriate_position={16.5}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="report_coverage_rate"
                        display_prefix="週間レポート"
                        display_title="閲覧率"
                        type="percent"
                        values={targetIndicatorValues("report_coverage_rate")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        upper_limit
                        appropriate={30}
                        appropriate_position={20}
                        button={false}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.growth.report_coverage_rate`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="report_digging_count"
                        display_prefix="レポート"
                        display_title="深掘り回数"
                        type="count"
                        values={targetIndicatorValues("report_digging_count")}
                        labels={[0, 2, 4, 6, 8, 10, 12, 14, 16]}
                        upper_limit
                        appropriate={50}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.growth.report_digging_rate`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        改善進行度
                      </div>
                    </div>
                    <div className="block-detail">
                      <div className="improvement_graph">
                        <div className="left">
                          <div className="item">
                            <div>
                              業務効率適正度
                            </div>
                            <div className="point">
                              {Math.floor(data?.improvement_progress?.efficiency_score || 0)}
                            </div>
                          </div>
                          <div className="item">
                            <div>
                              業務負荷適正度
                            </div>
                            <div className="point">
                              {Math.floor(data?.improvement_progress?.stress_score || 0)}
                            </div>
                          </div>
                          <div className="item">
                            <div>
                              業務集中適正度
                            </div>
                            <div className="point">
                              {Math.floor(data?.improvement_progress?.focus_score || 0)}
                            </div>
                          </div>
                          <div className="item">
                            <div>
                              平均
                            </div>
                            <div className="point">
                              {Math.floor(data?.improvement_progress?.total_average || 0)}
                            </div>
                          </div>
                        </div>
                        <div className="right">
                          <div className="background">
                            <table>
                              <tbody>
                                {
                                  Array(5).fill('').map((_v, i) => (
                                    <tr
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`improvement_${i}`}
                                    >
                                      {
                                        Array((displayDayList.length || 1) - 1).fill('').map((_w, j) => (
                                          <td
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`improvement_${i}_${j}`}
                                          />
                                        ))
                                      }
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                          </div>
                          <canvas
                            className="canvas"
                            ref={canvasRef}
                          />
                          <>
                            <div className="vertical_label">
                              100
                            </div>
                            <div className="vertical_label">
                              80
                            </div>
                            <div className="vertical_label">
                              60
                            </div>
                            <div className="vertical_label">
                              40
                            </div>
                            <div className="vertical_label">
                              20
                            </div>
                            <div className="vertical_label">
                              0
                            </div>
                          </>
                          {
                            displayDayList.map((value, index) => (
                              <React.Fragment key={`improvement_progress_${value}`}>
                                <div
                                  className="horizontal_label"
                                  style={{
                                    left: `${((index) / ((displayDayList.length || 1) - 1)) * 100}%`,
                                  }}
                                >
                                  {value}
                                </div>
                                {
                                  data?.improvement_progress?.values.filter((v) => v.date === value).map((val) => (
                                    <>
                                      {
                                        displayDayList.length !== index + 1 ? (
                                          <div
                                            className="past_mark"
                                            style={{
                                              top: `${100 - val.value}%`,
                                              left: `${((index) / ((displayDayList.length || 1) - 1)) * 100}%`,
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <div
                                              className="current_mark"
                                              style={{
                                                top: `${100 - val.value}%`,
                                              }}
                                            />
                                            <div
                                              className="current_point"
                                              style={{
                                                top: `${85 - val.value}%`,
                                              }}
                                            >
                                              {Math.floor(val.value)}
                                            </div>
                                          </>
                                        )
                                      }
                                    </>
                                  ))
                                }
                              </React.Fragment>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default MonthlyGrowth
